import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useBackendAuthenticated } from "@/hooks/useBackendAuthenticated";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const MedicalDrugSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  nameKz: z.string().nullable().optional(),
  inn: z.string().nullable(),
  manufacturer: z.string().nullable(),
  createdAt: z.string(),
});

export type MedicalDrug = z.infer<typeof MedicalDrugSchema>;

const ListResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(MedicalDrugSchema),
});

const ItemResponseSchema = z.object({
  success: z.boolean(),
  data: MedicalDrugSchema,
});

export function useMedicalDrugs() {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["medical-drugs"],
    queryFn: async () => {
      const json = await backend({ method: "GET", url: "/api/v1/medical-drugs" });
      return ListResponseSchema.parse(json).data;
    },
  });
}

export function useCreateMedicalDrug() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (data: { name: string; nameKz?: string; inn: string; manufacturer?: string }) =>
      backend({ method: "POST", url: "/api/v1/medical-drugs", data })
        .then((json) => ItemResponseSchema.parse(json).data),
    onSuccess: () => {
      toast.success(t("drugs.created"));
      queryClient.invalidateQueries({ queryKey: ["medical-drugs"] });
    },
  });
}

export function useUpdateMedicalDrug() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; nameKz?: string; inn?: string; manufacturer?: string }) =>
      backend({ method: "PATCH", url: `/api/v1/medical-drugs/${id}`, data })
        .then((json) => ItemResponseSchema.parse(json).data),
    onSuccess: () => {
      toast.success(t("drugs.updated"));
      queryClient.invalidateQueries({ queryKey: ["medical-drugs"] });
    },
  });
}

export function useDeleteMedicalDrug() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id: string) =>
      backend({ method: "DELETE", url: `/api/v1/medical-drugs/${id}` }),
    onSuccess: () => {
      toast.success(t("drugs.deleted"));
      queryClient.invalidateQueries({ queryKey: ["medical-drugs"] });
    },
  });
}
